import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HttpService } from '@core/services';
import { PermittingHttpService } from '@core/services/http';
import { JurisdictionActions } from '@store/actions';
import { JurisdictionSelectors } from '@store/selectors';

type Tab = 'zoning' | 'permitting' | 'contacts';

export const useJurisdictionTabPrefetch = (
  currentTab: Tab,
  jurisdictionPath: string,
) => {
  const dispatch = useDispatch();

  const zoningLoaded = useSelector(
    JurisdictionSelectors.isTabDataLoaded('zoning'),
  );

  const permittingLoaded = useSelector(
    JurisdictionSelectors.isTabDataLoaded('permitting'),
  );

  const contactsLoaded = useSelector(
    JurisdictionSelectors.isTabDataLoaded('contacts'),
  );

  useEffect(() => {
    const prefetchDelay = 1000;

    const prefetchMap: Partial<Record<Tab, Tab[]>> = {
      zoning: ['permitting', 'contacts'],
      permitting: ['contacts'],
      contacts: ['permitting'],
    };

    const loadingStates = {
      zoning: zoningLoaded,
      contacts: contactsLoaded,
      permitting: permittingLoaded,
    };

    const timer = setTimeout(() => {
      prefetchMap[currentTab]?.forEach(async (tabName: Tab) => {
        if (!loadingStates[tabName as keyof typeof loadingStates]) {
          dispatch(
            JurisdictionActions.setTabLoading.done({
              tabName,
              isLoading: true,
            }),
          );

          switch (tabName) {
            case 'contacts':
              await HttpService.getHttpRequests(
                PermittingHttpService,
              ).getJurisdictionContacts(jurisdictionPath, {
                silent: true,
              });

              break;

            case 'permitting':
              await HttpService.getHttpRequests(
                PermittingHttpService,
              ).getJurisdictionPermitting(jurisdictionPath, {
                silent: true,
              });

              break;

            default:
              break;
          }
        }
      });
    }, prefetchDelay);

    return () => clearTimeout(timer);
  }, [currentTab, zoningLoaded, contactsLoaded, permittingLoaded]);
};
